import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab,
  Paper, Table, TableHead, TableRow, TableCell, TableBody, ToggleButtonGroup, ToggleButton,
} from '@mui/material'
import { Add, MoreVert, Edit } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  listCashAccounts, listRecurringPaymentSchedules, type CashAccount, type RecurringPaymentSchedule,
  getCashflowAlerts, getCashflowForecast, type CashflowHorizon,
} from '../../api/financiar'
import type { AlertItem, AlertSeverity } from '../../api/rapoarte'
import CashAccountFormDialog from './CashAccountFormDialog'
import RecurringScheduleFormDialog from './RecurringScheduleFormDialog'

const INTERVAL_LABEL: Record<string, string> = { Lunar: 'Lunar', Trimestrial: 'Trimestrial', Semestrial: 'Semestrial', Anual: 'Anual' }

export default function CashflowPage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Financiar</Typography>
        <Typography variant="inherit" color="text.primary">Cash-flow</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Cash-flow</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Conturi și casierie" />
        <Tab label="Plăți recurente" />
        <Tab label="Alerte de scadență" />
        <Tab label="Prognoză" />
      </Tabs>
      {tab === 0 && <ConturiTab />}
      {tab === 1 && <ProgramariTab />}
      {tab === 2 && <AlerteScadentaTab />}
      {tab === 3 && <PrognozaTab />}
    </Stack>
  )
}

function ConturiTab() {
  const [formTarget, setFormTarget] = useState<CashAccount | null | 'new'>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts })

  const columns = useMemo<GridColDef<CashAccount>[]>(() => [
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'kind', headerName: 'Tip', width: 130, renderCell: (p) => p.row.kind === 'Bancar' ? 'Cont bancar' : 'Casierie' },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'iban', headerName: 'IBAN', width: 220, renderCell: (p) => p.row.iban ?? '—' },
    { field: 'balance', headerName: 'Sold', width: 130, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive ? <Chip label="Activ" size="small" color="success" variant="outlined" /> : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => <IconButton size="small" onClick={() => setFormTarget(p.row)}><Edit fontSize="small" /></IconButton>,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Cont nou</Button>
      </Box>
      <Box sx={{ height: 460, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <CashAccountFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
    </Stack>
  )
}

function ProgramariTab() {
  const [formTarget, setFormTarget] = useState<RecurringPaymentSchedule | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: RecurringPaymentSchedule } | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'programari-plati'], queryFn: listRecurringPaymentSchedules })
  const closeMenu = () => setMenuAnchor(null)

  const columns = useMemo<GridColDef<RecurringPaymentSchedule>[]>(() => [
    { field: 'description', headerName: 'Descriere', flex: 1, minWidth: 200 },
    { field: 'supplierName', headerName: 'Furnizor', width: 180, renderCell: (p) => p.row.supplierName ?? '—' },
    { field: 'amount', headerName: 'Sumă', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    { field: 'intervalType', headerName: 'Interval', width: 120, renderCell: (p) => INTERVAL_LABEL[p.row.intervalType] },
    { field: 'nextDueDate', headerName: 'Următoarea scadență', width: 160 },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive ? <Chip label="Activă" size="small" color="success" variant="outlined" /> : <Chip label="Inactivă" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Programare nouă</Button>
      </Box>
      <Box sx={{ height: 460, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <RecurringScheduleFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Stack>
  )
}

// ---------- Alerte de scadență (FR-FCF-007) ----------

const SEVERITY_LABEL: Record<AlertSeverity, string> = { Critical: 'Critice', Warning: 'Avertismente', Info: 'Informative' }
const SEVERITY_COLOR: Record<AlertSeverity, 'error' | 'warning' | 'info'> = { Critical: 'error', Warning: 'warning', Info: 'info' }
const SEVERITY_ORDER: AlertSeverity[] = ['Critical', 'Warning', 'Info']

function AlerteScadentaTab() {
  const navigate = useNavigate()
  const { data } = useQuery({ queryKey: ['financiar', 'cashflow', 'alerte'], queryFn: getCashflowAlerts })

  const groups = SEVERITY_ORDER.map((sev) => ({ severity: sev, items: (data ?? []).filter((a) => a.severity === sev) }))

  return (
    <Stack spacing={2}>
      {data && data.length === 0 && (
        <Box sx={{ color: 'text.secondary' }}>Niciun eveniment de scadență activ.</Box>
      )}
      {groups.filter((g) => g.items.length > 0).map((g) => (
        <Paper key={g.severity} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
            <Chip size="small" label={SEVERITY_LABEL[g.severity]} color={SEVERITY_COLOR[g.severity]} variant="outlined" />
            <Typography variant="subtitle2">{g.items.length} eveniment(e)</Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titlu</TableCell><TableCell>Detaliu</TableCell>
                <TableCell align="right">Cantitate / Sumă</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {g.items.map((a: AlertItem, i) => (
                <TableRow key={`${a.type}-${i}`}>
                  <TableCell>{a.title}</TableCell>
                  <TableCell>{a.detail}</TableCell>
                  <TableCell align="right">
                    {a.amount != null ? `${a.amount.toFixed(2).replace('.', ',')}${a.currency ? ` ${a.currency}` : ''}` : a.count != null ? a.count : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(a.linkPath)}>Deschide</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}
    </Stack>
  )
}

// ---------- Prognoză (FR-FCF-008/009) ----------

const HORIZON_LABEL: Record<CashflowHorizon, string> = { Saptamanal: 'Săptămânal', Lunar: 'Lunar', Trimestrial: 'Trimestrial' }

function PrognozaTab() {
  const [horizon, setHorizon] = useState<CashflowHorizon>('Lunar')
  const { data, isLoading } = useQuery({
    queryKey: ['financiar', 'cashflow', 'prognoza', horizon],
    queryFn: () => getCashflowForecast(horizon),
  })

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup value={horizon} exclusive size="small" onChange={(_, v) => v && setHorizon(v)}>
        {(['Saptamanal', 'Lunar', 'Trimestrial'] as CashflowHorizon[]).map((h) => (
          <ToggleButton key={h} value={h}>{HORIZON_LABEL[h]}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      {data && (
        <Typography variant="subtitle2">Sold inițial: {data.soldInitial.toFixed(2).replace('.', ',')}</Typography>
      )}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Perioadă</TableCell>
              <TableCell align="right">Încasări estimate</TableCell>
              <TableCell align="right">Plăți estimate</TableCell>
              <TableCell align="right">Sold net</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && (data?.buckets ?? []).map((b) => (
              <TableRow key={b.periodStart}>
                <TableCell>{b.label}</TableCell>
                <TableCell align="right">{b.cashInEstimat.toFixed(2).replace('.', ',')}</TableCell>
                <TableCell align="right">{b.cashOutEstimat.toFixed(2).replace('.', ',')}</TableCell>
                <TableCell align="right" sx={{ color: b.soldNet < 0 ? 'error.main' : undefined, fontWeight: 600 }}>
                  {b.soldNet.toFixed(2).replace('.', ',')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  )
}
